import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import quotationApi from "api/quotationApi";
import { quotationActions } from "./quotationSlice";
import {
  CreateContractPayload,
  QuotationListParams,
  QuotationPayload,
  SelectOptionPayload,
  SendQuotationPayload,
  SendDocumentPayload,
  UploadSignedContractPayload,
} from "models/quotation";

function* handleGetQuotations(
  action: PayloadAction<QuotationListParams | undefined>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.getQuotations, action.payload);
    const rawData = response?.data;
    const isDirectArray = Array.isArray(rawData);

    const data = isDirectArray
      ? {
          success: true,
          data: rawData,
          total: rawData.length,
          page: action.payload?.page ?? 1,
          limit: action.payload?.limit ?? 10,
        }
      : rawData || {};

    const possibleList =
      Array.isArray(data?.data?.quotations)
        ? data.data.quotations
        : Array.isArray(data?.quotations)
          ? data.quotations
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];

    const quotations = possibleList;
    const total = data?.total ?? data?.data?.total ?? quotations.length;
    const page = data?.page ?? data?.data?.page ?? action.payload?.page ?? 1;
    const limit = data?.limit ?? data?.data?.limit ?? action.payload?.limit ?? 10;

    if (data?.success || quotations.length || total > 0 || isDirectArray) {
      yield put(
        quotationActions.getQuotationsSuccess({
          quotations,
          total,
          page,
          limit,
        })
      );
    } else {
      const msg = data?.message || "Failed to fetch quotations";
      yield put(quotationActions.getQuotationsFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to fetch quotations";
    yield put(quotationActions.getQuotationsFailed(message));
  }
}

function* handleGetQuotationById(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.getQuotationById, action.payload);
    const data = response?.data;

    if (data?.success || data?.quotation || data?._id) {
      const quotation = data?.data?.quotation || data?.quotation || data?.data || data;
      yield put(quotationActions.getQuotationByIdSuccess(quotation));
    } else {
      const msg = data?.message || "Failed to fetch quotation details";
      toast.error(msg);
      yield put(quotationActions.getQuotationByIdFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to fetch quotation details";
    toast.error(message);
    yield put(quotationActions.getQuotationByIdFailed(message));
  }
}

// 🔹 Step 1: Create Quotation (status: DRAFT)
function* handleCreateQuotation(
  action: PayloadAction<{ payload: QuotationPayload; callback?: (record?: any) => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.createQuotation, action.payload.payload);
    const data = response?.data;

    if (data?.success || data?.data?._id || data?._id) {
      const record = data?.data || data;
      toast.success(data?.message || "Quotation draft created successfully!");
      yield put(quotationActions.createQuotationSuccess(record));
      if (action.payload.callback) {
        action.payload.callback(record);
      }
    } else {
      const msg = data?.message || "Failed to create quotation";
      toast.error(msg);
      yield put(quotationActions.createQuotationFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to create quotation";
    toast.error(message);
    yield put(quotationActions.createQuotationFailed(message));
  }
}

// 🔹 Step 2: Update Quotation (Pass Quotation ID in DRAFT status)
function* handleUpdateQuotation(
  action: PayloadAction<{ payload: QuotationPayload; callback?: (record?: any) => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.updateQuotation, action.payload.payload);
    const data = response?.data;

    if (data?.success || data?.data?._id || data?._id) {
      const record = data?.data || data;
      toast.success(data?.message || "Quotation updated successfully!");
      yield put(quotationActions.updateQuotationSuccess(record));
      if (action.payload.callback) {
        action.payload.callback(record);
      }
    } else {
      const msg = data?.message || "Failed to update quotation";
      toast.error(msg);
      yield put(quotationActions.updateQuotationFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to update quotation";
    toast.error(message);
    yield put(quotationActions.updateQuotationFailed(message));
  }
}

// 🔹 Step 3: Send Quotation (Lock Editing, status => SENT)
function* handleSendQuotation(
  action: PayloadAction<{ payload: SendQuotationPayload; callback?: (record?: any) => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.sendQuotation, action.payload.payload);
    const data = response?.data;

    if (data?.success || data?.data) {
      toast.success(data?.message || "Quotation sent successfully! Editing is now locked.");
      yield put(
        quotationActions.sendQuotationSuccess({
          quotationId: action.payload.payload.quotationId || action.payload.payload.id || "",
        })
      );
      if (action.payload.callback) {
        action.payload.callback(data?.data || data);
      }
    } else {
      const msg = data?.message || "Failed to send quotation";
      toast.error(msg);
      yield put(quotationActions.sendQuotationFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to send quotation";
    toast.error(message);
    yield put(quotationActions.sendQuotationFailed(message));
  }
}

// 🔹 Send Document (Quotation or Contract via PATCH /quotation/sendDocument)
function* handleSendDocument(
  action: PayloadAction<{ payload: SendDocumentPayload; callback?: (record?: any) => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.sendDocument, action.payload.payload);
    const data = response?.data;

    if (data?.success || data?.data) {
      const docLabel = action.payload.payload.type === "CONTRACT" ? "Contract" : "Quotation";
      toast.success(data?.message || `${docLabel} sent successfully!`);
      yield put(
        quotationActions.sendDocumentSuccess({
          id: action.payload.payload.id,
          type: action.payload.payload.type,
        })
      );
      if (action.payload.callback) {
        action.payload.callback(data?.data || data);
      }
    } else {
      const msg = data?.message || "Failed to send document";
      toast.error(msg);
      yield put(quotationActions.sendDocumentFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to send document";
    toast.error(message);
    yield put(quotationActions.sendDocumentFailed(message));
  }
}

// 🔹 Step 4: Select Option (Accept chosen option, status => ACCEPTED)
function* handleSelectOption(
  action: PayloadAction<{ payload: SelectOptionPayload; callback?: (result: any) => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.selectOption, action.payload.payload);
    const data = response?.data;

    if (data?.success || data?.data?.invoice || data?.invoice || data?.data?.quotation) {
      const result = data?.data || data;
      toast.success(
        data?.message ||
          "Proposal option accepted! Status updated to ACCEPTED."
      );
      yield put(quotationActions.selectOptionSuccess(result));
      if (action.payload.callback) {
        action.payload.callback(result);
      }
    } else {
      const msg = data?.message || "Failed to select quotation option";
      toast.error(msg);
      yield put(quotationActions.selectOptionFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to accept quotation option";
    toast.error(message);
    yield put(quotationActions.selectOptionFailed(message));
  }
}

// 🔹 Step 5: Create Formal Contract (status => PENDING_SIGNATURE)
function* handleCreateContract(
  action: PayloadAction<{ payload: CreateContractPayload; callback?: (contract: any) => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.createContract, action.payload.payload);
    const data = response?.data;

    if (data?.success || data?.data?.contract || data?.contract) {
      const contract = data?.data?.contract || data?.contract || data?.data;
      toast.success(data?.message || "Formal contract generated successfully (Pending Signature)!");
      yield put(
        quotationActions.createContractSuccess({
          quotationId: action.payload.payload.quotationId,
          contract,
        })
      );
      if (action.payload.callback) {
        action.payload.callback(contract);
      }
    } else {
      const msg = data?.message || "Failed to create formal contract";
      toast.error(msg);
      yield put(quotationActions.createContractFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to create formal contract";
    toast.error(message);
    yield put(quotationActions.createContractFailed(message));
  }
}

// 🔹 Step 6: Upload Signed Contract & Activate (status => ACTIVE)
function* handleUploadSignedContract(
  action: PayloadAction<{ payload: UploadSignedContractPayload; callback?: (res: any) => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.uploadSignedContract, action.payload.payload);
    const data = response?.data;

    if (data?.success || data?.data) {
      const contract = data?.data?.contract || data?.contract || data?.data;
      toast.success(data?.message || "Signed contract attached and activated successfully!");
      yield put(
        quotationActions.uploadSignedContractSuccess({
          contractId: action.payload.payload.contractId,
          contract,
        })
      );
      if (action.payload.callback) {
        action.payload.callback(data);
      }
    } else {
      const msg = data?.message || "Failed to upload signed contract";
      toast.error(msg);
      yield put(quotationActions.uploadSignedContractFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to upload signed contract";
    toast.error(message);
    yield put(quotationActions.uploadSignedContractFailed(message));
  }
}

// 🔹 Cancel
function* handleCancelQuotation(
  action: PayloadAction<{ id: string; callback?: () => void }>
): Generator<any, void, any> {
  try {
    const response: any = yield call(quotationApi.cancelQuotation, action.payload.id);
    const data = response?.data;

    if (data?.success) {
      toast.success(data?.message || "Quotation cancelled successfully");
      yield put(quotationActions.cancelQuotationSuccess({ id: action.payload.id }));
      if (action.payload.callback) {
        action.payload.callback();
      }
    } else {
      const msg = data?.message || "Failed to cancel quotation";
      toast.error(msg);
      yield put(quotationActions.cancelQuotationFailed(msg));
    }
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || "Failed to cancel quotation";
    toast.error(message);
    yield put(quotationActions.cancelQuotationFailed(message));
  }
}

export default function* quotationSaga() {
  yield takeLatest(quotationActions.getQuotations.type, handleGetQuotations);
  yield takeLatest(quotationActions.getQuotationById.type, handleGetQuotationById);
  yield takeLatest(quotationActions.createQuotation.type, handleCreateQuotation);
  yield takeLatest(quotationActions.updateQuotation.type, handleUpdateQuotation);
  yield takeLatest(quotationActions.sendQuotation.type, handleSendQuotation);
  yield takeLatest(quotationActions.sendDocument.type, handleSendDocument);
  yield takeLatest(quotationActions.selectOption.type, handleSelectOption);
  yield takeLatest(quotationActions.createContract.type, handleCreateContract);
  yield takeLatest(quotationActions.uploadSignedContract.type, handleUploadSignedContract);
  yield takeLatest(quotationActions.cancelQuotation.type, handleCancelQuotation);
}
